import * as React from 'react';
import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { TestProjectInputEditContent } from './TestProjectInputEditContent';
import { TestProjectInputMappingContent } from './TestProjectInputMappingContent';
import type TestProjectInputEditorDraft from '../../models/TestProjectInputEditorDraft';
import type TestProjectInputEditorStore from '../../stores/TestProjectInputEditorStore';

type Props = {
    store: TestProjectInputEditorStore;
    draft: TestProjectInputEditorDraft;
    confirmation?: boolean;
};

type ActionsProps = Props & {
    onMapInputs: () => void;
    onCancel: () => void;
    saveLabel?: string;
};

// Both dialog shells use the same editor phases and actions. The caller owns dismissal and the save label.
export const TestProjectInputEditorContent = observer(
    ({ store, draft, confirmation = draft.showsConfirmation }: Props) => {
        if (confirmation && draft.pendingTopicRemoval) {
            return <p>Removing this topic will clear all of its configured baselines when you save.</p>;
        }

        if (draft.operation === 'mapping') {
            return <TestProjectInputMappingContent store={store} draft={draft} confirmation={confirmation} />;
        }

        return <TestProjectInputEditContent store={store} draft={draft} confirmation={confirmation} />;
    }
);

export const TestProjectInputEditorActions = observer(
    ({
        store,
        draft,
        onMapInputs,
        onCancel,
        saveLabel = 'Save',
        confirmation = draft.showsConfirmation
    }: ActionsProps) => {
        if (confirmation && draft.pendingTopicRemoval) {
            return (
                <>
                    <Button className="light" size="large" onClick={store.cancelTopicRemoval}>
                        Back to editing
                    </Button>
                    <Button
                        className="dark"
                        danger
                        disabled={!store.isSourceEditable}
                        onClick={store.confirmTopicRemoval}
                        size="large"
                        type="primary"
                    >
                        Remove topic
                    </Button>
                </>
            );
        }

        if (confirmation) {
            const mapping = draft.operation === 'mapping';

            return (
                <>
                    {!mapping && (
                        <Button className="light" size="large" onClick={onMapInputs} disabled={store.isSubmitting}>
                            Map inputs · keep baselines
                        </Button>
                    )}
                    <Button className="light" size="large" onClick={store.backToEditing} disabled={store.isSubmitting}>
                        {mapping ? 'Back to mapping' : 'Back to editing'}
                    </Button>
                    <Button
                        className="dark"
                        danger
                        disabled={!store.isSourceEditable}
                        loading={store.isSubmitting}
                        onClick={store.confirmSave}
                        size="large"
                        type="primary"
                    >
                        {mapping ? 'Continue and delete unmapped baselines' : 'Delete baselines and save'}
                    </Button>
                </>
            );
        }

        return (
            <>
                <Button className="light" size="large" onClick={onCancel} disabled={store.isSubmitting}>
                    Cancel
                </Button>
                <Button
                    className="dark"
                    disabled={!store.canSave}
                    loading={store.isSubmitting || draft.isPreparing}
                    onClick={store.prepareSave}
                    size="large"
                    type="primary"
                >
                    {saveLabel}
                </Button>
            </>
        );
    }
);

export function getTestProjectInputEditorTitle(
    draft: TestProjectInputEditorDraft,
    confirmation = draft.showsConfirmation
) {
    const projectName = draft.originalProject.name;

    if (confirmation && draft.pendingTopicRemoval) {
        return `Remove topic from ${projectName}?`;
    }

    if (draft.operation === 'mapping') {
        return confirmation
            ? `Confirm baseline deletion for ${projectName}`
            : `Map inputs for ${projectName} and keep baselines`;
    }

    return confirmation ? `Delete existing baselines for ${projectName}?` : `Edit topics for ${projectName}`;
}
